import { useState, useEffect, useCallback, useContext } from 'react';
import { LoaderContext } from '../contexts/LoaderContext';
import { ModalsContext } from '../contexts/ModalsContext';
import { AlertContext } from '../contexts/AlertContext';
import { getSessionstorage } from '../helpers/sessionStorage';
import { convertirLetraMayus } from '../helpers/convertirLetraMayus';
import { CreateUserForm } from '../components/user/CreateUserForm';
import { Actions } from '../components/user/Actions';
import { useModalStore } from '../store/useModalStore';

const columns = [
    { id: "nombre", label: "Nombres y Apellidos", minWidth: 170, align: 'center' },
    { id: "correo", label: "Correo", minWidth: 170, align: 'center' },
    { id: "area", label: "Area", minWidth: 100, align: 'center' },
    { id: "sede", label: "Sede", minWidth: 100, align: 'center' },
    { id: "rol", label: "Rol", minWidth: 100, align: 'center' },
    { id: "username", label: "Usuario", minWidth: 100, align: 'center' },
    { id: "acciones", label: "Acciones", minWidth: 170, align: 'center' }
];

export const useUsers = () => {

    const [users, setUsers] = useState([]);
    const { setOpenLoader } = useContext(LoaderContext);
    // const { modalRef } = useContext(ModalsContext);
    const { alertRef } = useContext(AlertContext);
    const { openModal, closeModal } = useModalStore();

    useEffect(() => {
        let unsubscribed = true;
      
        if(unsubscribed) getUsers();

        return () => unsubscribed = false;
    }, []);

    const getUsers = useCallback(async () => {
        try {
            setOpenLoader(true);
            const response = await fetch(`/api/users`);
            const { ok, message, data } = await response.json();

            if(!ok) {
                setOpenLoader(false);
                alertRef.current.handleOpen('error', message);
                return;
            }

            setUsers(data?.map(generatePayload));
            setOpenLoader(false);
        } catch (error) {
            throw new Error(error.message);
        }
    }, []);

    // const addUser = () => modalRef.current.handleOpen(<CreateUserForm action={ saveUser } />, 480, 'auto');
    const addUser = () => openModal(<CreateUserForm action={ saveUser } />, 480);

    const saveUser = async ({ nombre, username, correo, ...rest}) => {
        try {
            setOpenLoader(true);
           
            const payload = {
                nombre: nombre.toLowerCase(),
                username: username.toLowerCase(),
                correo: correo.toLowerCase(),
                ...rest
            };

            const response = await fetch(`/api/users`, {
                method: 'POST',
                body: JSON.stringify(payload),
                headers: {
                    "Content-Type": 'application/json',
                    token: await getSessionstorage()
                }
            });
            
            const { ok, message, data } = await response.json();

            if(!ok) {
                setOpenLoader(false);
                // modalRef.current?.handleClose();
                closeModal();
                alertRef.current?.handleOpen('error', message);
                return;
            }

            setUsers(prev => ([...prev, generatePayload(data)]));
            // modalRef.current?.handleClose();
            closeModal();
            setOpenLoader(false);         
        } catch (error) {
            throw new Error(error.message);
        }
    }

    const updateUser = async (fields, uid) => {
        setOpenLoader(true);
        
        try {
            const response = await fetch(`/api/users/${ uid }`, {
                method: 'PUT',
                body: JSON.stringify(fields),
                headers: {
                    "Content-Type": 'application/json',
                    token: await getSessionstorage()
                }
            });

            const { ok, message, data } = await response.json();

            if(!ok) {
                setOpenLoader(false);
                // modalRef.current?.handleClose();
                closeModal();
                alertRef.current?.handleOpen('error', message);
                return;
            }

            setUsers(prev => prev?.map(p => p?.uid === uid ? generatePayload(data) : p ));
            // modalRef.current?.handleClose();
            closeModal();
            setOpenLoader(false);
        } catch (error) {
            throw new Error(error.message);
        }
    }

    const permissionsUser = async (fields, uid) => {
        setOpenLoader(true);

        try {
            const response = await fetch(`/api/users/permissions/${ uid }`, {
                method: 'PUT',
                body: JSON.stringify({ permisos: fields }),
                headers: {
                    "Content-Type": 'application/json',
                    token: await getSessionstorage()
                }
            });

            const { ok, message, data } = await response.json();

            if(!ok) {
                setOpenLoader(false);
                // modalRef.current?.handleClose();
                closeModal();
                alertRef.current?.handleOpen('error', message);
                return;
            }

            setUsers(prev => prev?.map(p => p?.uid === uid ? generatePayload(data) : p ));
            // modalRef.current?.handleClose();
            closeModal();
            setOpenLoader(false);
        } catch (error) {
            throw new Error(error.message);
        }
    }

    const deleteUser = async (uid) => {
        setOpenLoader(true);
        try {   
            const response = await fetch(`/api/users/${ uid }`, {
                method: 'DELETE',
                headers: {
                    token: await getSessionstorage()
                }
            });

            const { ok, message } = await response.json();

            if(!ok) {
                setOpenLoader(false);
                alertRef.current.handleOpen('error', message);
                return;
            }

            setUsers(prev => prev?.filter(user => user.uid !== uid));
            // modalRef.current?.handleClose();
            closeModal();
            setOpenLoader(false);
        }catch(error) {
            throw new Error(error.message);
        }
    }

    const generatePayload = (data) => ({
        ...data,
        nombre: convertirLetraMayus(data?.nombre, true),
        sede: convertirLetraMayus(data?.sede?.nombre),
        area: convertirLetraMayus(data?.area?.nombre),
        rol: data?.rol?.nombre,
        acciones: <Actions data={ data } permissions={ permissionsUser } updated={ updateUser } deleted={ deleteUser } />
    });

    return {
        columns,
        users,
        addUser,
        permissionsUser,
        updateUser,
        deleteUser
    }
}